import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TouristGuideRate, TouristGuideRatePayload } from '../_models/tourist-guides-rates.model';
import { TouristGuidesRatesApiService } from '../_api/tourist-guides-rates.api.service';
import { DateService } from '@/common/date.service';
import { PolishDateAdapter } from '@/common/polish-date-adapter/polish-date-adapter';
import { DateAdapter } from '@angular/material/core';
import { catchError, finalize, throwError } from 'rxjs';
import { dateRangeValidator } from '@/common/validators/date-range.validator';

interface DialogData {
	rate: TouristGuideRate | null;
	mode: 'add' | 'edit';
	touristGuideId: number;
}

@Component({
	selector: 'app-tourist-guides-rate-dialog',
	standalone: true,
	imports: [
		CommonModule,
		ReactiveFormsModule,
		MatDialogModule,
		MatFormFieldModule,
		MatInputModule,
		MatButtonModule,
		MatDatepickerModule,
		MatProgressSpinnerModule,
	],
	providers: [{ provide: DateAdapter, useClass: PolishDateAdapter }],
	templateUrl: './tourist-guides-rate-dialog.component.html',
	styleUrl: './tourist-guides-rate-dialog.component.scss',
})
export class TouristGuidesRateDialogComponent implements OnInit {
	public dialogRef = inject(MatDialogRef<TouristGuidesRateDialogComponent>);
	public data = inject<DialogData>(MAT_DIALOG_DATA);
	rateForm: FormGroup;
	readonly isEditMode = signal(false);
	readonly isSaving = signal(false);
	readonly error = signal<string | null>(null);
	readonly title = computed(() => (this.isEditMode() ? 'Edytuj stawkę' : 'Dodaj nową stawkę'));
	private fb = inject(FormBuilder);
	private apiService = inject(TouristGuidesRatesApiService);
	private dateService = inject(DateService);

	ngOnInit(): void {
		this.isEditMode.set(this.data.mode === 'edit');
		this.initForm();
	}

	initForm(): void {
		this.rateForm = this.fb.group(
			{
				dateFrom: [this.data.rate?.dateFrom, Validators.required],
				dateTo: [this.data.rate?.dateTo],
				rate: [
					this.data.rate?.rate ? this.data.rate.rate / 100 : null,
					[Validators.required, Validators.min(0)],
				],
				comments: [this.data.rate?.comments || ''],
			},
			{ validators: dateRangeValidator },
		);
	}

	onCancel(): void {
		this.dialogRef.close();
	}

	onSave(): void {
		if (this.rateForm.invalid || this.isSaving()) {
			return;
		}

		const formValue = this.rateForm.value;
		const payload: TouristGuideRatePayload = {
			date_from: this.formatDate(formValue.dateFrom),
			date_to: formValue.dateTo ? this.formatDate(formValue.dateTo) : null,
			rate: Math.round(formValue.rate * 100),
			comments: formValue.comments || '',
		};

		this.isSaving.set(true);
		this.error.set(null);

		const request = this.isEditMode()
			? this.apiService.updateRate(this.data.touristGuideId, this.data.rate!.id, payload)
			: this.apiService.createRate(this.data.touristGuideId, payload);

		request
			.pipe(
				catchError((err) => {
					this.error.set(
						this.isEditMode() ? 'Nie udało się zaktualizować stawki' : 'Nie udało się dodać stawki',
					);
					return throwError(() => err);
				}),
				finalize(() => this.isSaving.set(false)),
			)
			.subscribe(() => {
				this.dialogRef.close(true);
			});
	}

	private formatDate(date: Date | string): string {
		if (typeof date === 'string') {
			return date;
		}
		return this.dateService.datetimeWithTimezone(date).date;
	}
}
